import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

const DESKTOP_MQ = '(min-width: 640px)'; // Tailwind sm 브레이크포인트와 동일

/* Figma asset: Pagination component - 32x32 buttons, active bg:#F1F3F7, r:8
 * 윈도잉: total 페이지가 윈도우 사이즈보다 많으면 현재 페이지가 속한 윈도우만 노출.
 *  - 데스크톱(sm↑): windowSize 적용 (기본 10), 모바일: mobileWindowSize 적용 (기본 5)
 *  - 예) total=16, current=5 → 모바일 [1..5] / 데스크톱 [1..10]
 *  - <,> 는 단일 페이지 이동. 윈도우 경계에서 자동으로 다음/이전 윈도우로 넘어감.
 *  - SSR-safe: react-snap prerender 시 window 미정의 → 기본 데스크톱 가정 후 hydrate 시 보정. */
export default function Pagination({
  current = 1,
  total = 1,
  onChange,
  windowSize = 10,
  mobileWindowSize = 5,
  className = '',
  // alwaysShow=true 면 total<=1 이어도 컴포넌트 렌더 (어드민 일부 페이지에서
  // 일관된 페이지네이션 UI 노출이 필요한 케이스용).
  alwaysShow = false,
}) {
  const { t } = useTranslation();
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(DESKTOP_MQ).matches : true,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(DESKTOP_MQ);
    const onChangeMQ = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChangeMQ);
    return () => mq.removeEventListener('change', onChangeMQ);
  }, []);

  if (!alwaysShow && total <= 1) return null;

  // alwaysShow 모드에서 total 이 0/1 이어도 최소 1페이지로 표기 (이전/다음 disabled).
  const effectiveTotal = Math.max(total, 1);
  const effectiveWindow = isDesktop ? windowSize : mobileWindowSize;
  const safeCurrent = Math.min(Math.max(1, current), effectiveTotal);
  const windowStart = Math.floor((safeCurrent - 1) / effectiveWindow) * effectiveWindow + 1;
  const windowEnd = Math.min(windowStart + effectiveWindow - 1, effectiveTotal);

  const pages = [];
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  const hasPrevWindow = windowStart > 1;
  const hasNextWindow = windowEnd < effectiveTotal;

  return (
    <div className={`flex items-center gap-1.5 sm:gap-2 justify-center flex-wrap ${className}`}>
      <button
        onClick={() => safeCurrent > 1 && onChange(safeCurrent - 1)}
        disabled={safeCurrent <= 1}
        aria-label={t('common.pagination_prev')}
        className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-mono-400 hover:bg-mono-100 disabled:opacity-30 transition"
      >
        <Icon name="chevron-left" size={16} />
      </button>
      {hasPrevWindow && (
        <button
          onClick={() => onChange(windowStart - 1)}
          aria-label={t('common.pagination_prev_n', { n: effectiveWindow })}
          className="px-2 h-9 sm:h-8 flex items-center justify-center rounded-lg text-xs text-mono-400 hover:bg-mono-100 transition"
        >
          ...
        </button>
      )}
      {pages.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-sm transition ${
            p === safeCurrent ? 'bg-mono-100 font-bold text-mono-900' : 'text-mono-400 hover:bg-mono-100'
          }`}
        >
          {p}
        </button>
      ))}
      {hasNextWindow && (
        <button
          onClick={() => onChange(windowEnd + 1)}
          aria-label={t('common.pagination_next_n', { n: effectiveWindow })}
          className="px-2 h-9 sm:h-8 flex items-center justify-center rounded-lg text-xs text-mono-400 hover:bg-mono-100 transition"
        >
          ...
        </button>
      )}
      <button
        onClick={() => safeCurrent < effectiveTotal && onChange(safeCurrent + 1)}
        disabled={safeCurrent >= effectiveTotal}
        aria-label={t('common.pagination_next')}
        className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-mono-400 hover:bg-mono-100 disabled:opacity-30 transition"
      >
        <Icon name="chevron-right" size={16} />
      </button>
    </div>
  );
}
